import { CompanyInfo, VerificationResult, PeppolResult, DualVerificationResult, DualBatchResult } from '../types';
import { VatVerificationService } from './vatVerificationService';
import { PeppolVerificationService } from './peppolVerificationService';
import { SupportedLanguage } from '../utils/errorTranslations';
import { Analytics, ErrorTracker, PerformanceTracker } from '../utils/analytics';

export class DualVerificationService {
  static async verifyBoth(
    identifier: string,
    companyName?: string,
    language: SupportedLanguage = 'en'
  ): Promise<DualVerificationResult> {
    
    try {
      Analytics.verificationStarted('dual', 'single');
      PerformanceTracker.startTimer(`dual_verification_${identifier}`);
      // Create company info for VAT verification
      const companyInfo: CompanyInfo = {
        name: companyName || '',
        vatNumber: identifier,
        address: '',
        country: '',
        city: '',
        postalCode: ''
      };

      // Run both verifications in parallel using the same identifier
      const [vatResult, peppolResult] = await Promise.all([
        VatVerificationService.verifyVatNumber(companyInfo, language),
        PeppolVerificationService.verifyPeppolId(identifier, language)
      ]);

      const responseTime = PerformanceTracker.endTimer(`dual_verification_${identifier}`, 'api_response_time', { service: 'dual' });
      console.log('Response time:', responseTime);
      // Track success/failure based on results
      const hasValidResult = vatResult.isValid || peppolResult.isValid;
      if (hasValidResult) {
        Analytics.verificationSuccess('dual', 'single');
      } else {
        Analytics.verificationError('dual', 'single', 'both_verifications_failed');
      }

      return {
        vatResult,
        peppolResult,
        timestamp: new Date().toISOString()
      };
    } catch (error) {
      const responseTime = PerformanceTracker.endTimer(`dual_verification_${identifier}`);
      console.log('Response time:', responseTime);
      Analytics.verificationError('dual', 'single', 'api_error');
      ErrorTracker.trackApiError('dual', error, 'verifyBoth');
      console.error('Dual verification error:', error);
      
      return {
        vatResult: {
          vatNumber: identifier,
          isValid: false,
          error: 'Verification failed',
          timestamp: new Date().toISOString()
        },
        peppolResult: {
          participantId: identifier,
          isValid: false,
          error: 'Verification failed',
          timestamp: new Date().toISOString()
        },
        timestamp: new Date().toISOString()
      };
    }
  }

  static async verifyBatchBoth(
    identifiers: Array<{ identifier: string; companyName?: string }>
  ): Promise<DualBatchResult> {
    const startTime = Date.now();
    const vatResults: VerificationResult[] = [];
    const peppolResults: PeppolResult[] = [];

    // Process in batches to avoid overwhelming the APIs
    const batchSize = 5;
    for (let i = 0; i < identifiers.length; i += batchSize) {
      const batch = identifiers.slice(i, i + batchSize);
      
      const batchPromises = batch.map(async ({ identifier, companyName }) => {
        const [vatResult, peppolResult] = await Promise.all([
          VatVerificationService.verifyVatNumber({
            name: companyName || '',
            vatNumber: identifier,
            address: '',
            country: '',
            city: '',
            postalCode: ''
          }),
          PeppolVerificationService.verifyPeppolId(identifier)
        ]);
        return { vatResult, peppolResult };
      });

      const batchResults = await Promise.all(batchPromises);
      
      batchResults.forEach(({ vatResult, peppolResult }) => {
        vatResults.push(vatResult);
        peppolResults.push(peppolResult);
      });

      // Add a small delay between batches to be respectful to the APIs
      if (i + batchSize < identifiers.length) {
        await new Promise(resolve => setTimeout(resolve, 1000));
      }
    }

    return {
      totalProcessed: identifiers.length,
      vatResults,
      peppolResults,
      processingTime: Date.now() - startTime
    };
  }

  static setUseRealAPI(useReal: boolean) {
    VatVerificationService.setUseRealAPI(useReal);
    PeppolVerificationService.setUseRealAPI(useReal);
  }
}
